'use client';

import { useEffect, useRef, useState } from 'react';

import { useRevealStageOpen } from './reveal-stage';
import { REVEAL } from './tokens';

/**
 * Where a page is in its load: skeletons, the cross-fade onto real content, then the choreography.
 * `playing` is what motion components wait for, so a figure never bounces while the fade is still running.
 */
type RevealPhase = 'loading' | 'revealing' | 'playing';

type RevealOptions = {
  /**
   * Latch the page once its reveal starts, and carry it through to `playing` even if readiness goes false again.
   *
   * Readiness is only monotonic when nothing refetches. A surface whose queries carry
   * `notifyOnNetworkStatusChange`, or that refetches on a filter, a range switch or a mutation, drops back
   * to loading on every one of those — and without this the whole page would replay its reveal each time.
   * The refetching surface should dim in place instead; that is what `SettleIn`'s `refreshing` is for.
   */
  readonly once?: boolean;
};

/**
 * One clock for a whole page. While `dataReady` is false every surface holds its skeleton; when it flips the
 * page cross-fades onto real content for `REVEAL.duration`, and only then does the choreography start. Without
 * this the fade and the first beat overlap, which is what reads as content popping rather than arriving.
 */
function useRevealSequence(dataReady: boolean, options: RevealOptions = {}): RevealPhase {
  const { once = false } = options;
  const [phase, setPhase] = useState<RevealPhase>('loading');
  const settled = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Data that lands while the opening splash is still up waits for it: the reveal is for the client to see.
  const stageOpen = useRevealStageOpen();
  const ready = dataReady && stageOpen;

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (once && settled.current) return;
    if (!ready) {
      setPhase('loading');
      return;
    }
    settled.current = once;
    setPhase('revealing');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setPhase('playing');
    }, REVEAL.duration * 1000);
    return () => {
      if (!once) clearTimeout(timer.current);
    };
  }, [ready, once]);

  return phase;
}

/** The delay a figure adds so its beat lands after the reveal fade, not under it. */
function revealOffset(phase: RevealPhase): number {
  return phase === 'loading' ? 0 : REVEAL.duration;
}

export { revealOffset, useRevealSequence };
export type { RevealOptions, RevealPhase };
